import type {StyleFunctionProps} from '@chakra-ui/styled-system';
import {createMultiStyleConfigHelpers} from '@chakra-ui/styled-system';
import {calc, mode} from '@chakra-ui/theme-tools';

import {tokens as dark} from '../../../generated/dark';
import {tokens as light} from '../../../generated/light';

import {alertAnatomy} from './anatomy';
import {vars} from './css-vars';

const tokens = {
  light: {...light.components.alert, ...light.components.alerts},
  dark: {...dark.components.alert, ...dark.components.alerts},
};

export const {definePartsStyle, defineMultiStyleConfig} = createMultiStyleConfigHelpers(alertAnatomy.keys());

const statusStyles = (props: StyleFunctionProps) => ({
  [`&[data-status="info"]`]: {
    [vars.background.variable]: mode(
      `colors.${tokens.light.light[`background-default`]}`,
      `colors.${tokens.dark.light[`background-default`]}`,
    )(props),
    [vars.color.variable]: `colors.text.primary`,
  },

  [`&[data-status="success"]`]: {
    [vars.background.variable]: mode(
      `colors.${tokens.light.success[`background-default`]}`,
      `colors.${tokens.dark.success[`background-default`]}`,
    )(props),
    [vars.color.variable]: mode(
      `colors.${tokens.light.success[`text-default`]}`,
      `colors.${tokens.dark.success[`text-default`]}`,
    )(props),
  },

  [`&[data-status="error"]`]: {
    [vars.background.variable]: mode(
      `colors.${tokens.light.error[`background-default`]}`,
      `colors.${tokens.dark.error[`background-default`]}`,
    )(props),
    [vars.color.variable]: mode(
      `colors.${tokens.light.error[`text-default`]}`,
      `colors.${tokens.dark.error[`text-default`]}`,
    )(props),
  },

  [`&[data-status="warning"]`]: {
    [vars.background.variable]: mode(
      `colors.${tokens.light.warning[`background-default`]}`,
      `colors.${tokens.dark.warning[`background-default`]}`,
    )(props),
    [vars.color.variable]: mode(
      `colors.${tokens.light.warning[`text-default`]}`,
      `colors.${tokens.dark.warning[`text-default`]}`,
    )(props),
  },
});
//TODO: use design token + dark theme
const baseStyle = definePartsStyle(props => {
  const isVertical = props.orientation === `vertical`;

  const horizontalStyles = definePartsStyle({
    container: {},
    content: {
      display: `flex`,
      flexWrap: `wrap`,
    },
    actions: {
      ml: `auto`,
      display: `flex`,
      alignItems: `center`,
      gap: `space-100`,
    },
    close: {},
  });

  const verticalStyles = definePartsStyle({
    container: {
      flexDirection: `column`,
      alignItems: `start`,
    },
    content: {
      display: `flex`,
      flexDirection: `column`,
      alignItems: `start`,
    },
    actions: {},
    close: {
      position: `absolute`,
      m: vars[`padding-top`].reference,
      right: 0,
      top: 0,
    },
  });

  return {
    container: {
      [vars.background.variable]: mode(
        `colors.${light.semantic.colors.background[`primary-inverse`]}`,
        `colors.${dark.semantic.colors.background[`primary-inverse`]}`,
      )(props),
      [vars.color.variable]: `colors.text.primary-inverse`,
      [vars.gap.variable]: `space.${tokens.light.gutter}`,

      [vars[`padding-left`].variable]: `space.space-200`,
      [vars[`padding-right`].variable]: `space.space-200`,
      [vars[`padding-top`].variable]: isVertical ? `space.space-150` : `space.space-200`,
      [vars[`padding-bottom`].variable]: isVertical ? `space.space-150` : `space.space-200`,
      [vars[`font-size`].variable]: `fontSizes.md`,
      [vars[`font-weight`].variable]: `400`,

      position: `relative`,

      // Colors
      background: vars.background.reference,
      color: vars.color.reference,

      // Spacing
      gap: vars.gap.reference,
      padding: `${vars[`padding-top`].reference} ${vars[`padding-right`].reference} ${
        vars[`padding-bottom`].reference
      }`,

      borderRadius: `radius-100`,

      // Typography
      fontSize: vars[`font-size`].reference,
      fontWeight: vars[`font-weight`].reference,
      '&': {
        paddingInlineEnd: vars[`padding-right`].reference,
        paddingInlineStart: vars[`padding-left`].reference,
      },

      ...(isVertical ? verticalStyles.container : horizontalStyles.container),
      ...(props.variant !== `primary` ? statusStyles(props) : {}),
    },
    title: {
      fontWeight: `600`,
    },
    icon: {
      fontSize: `1.5rem`,
      w: `1em`,
      h: `1em`,
      svg: {
        display: `block`,
      },
    },
    content: {
      columnGap: `space-100`,
      ...(isVertical ? verticalStyles.content : horizontalStyles.content),
    },
    actions: {
      ...(isVertical ? verticalStyles.actions : horizontalStyles.actions),
    },
    close: {
      color: vars.in.reference,
      _hover: {
        color: vars.in.reference,
      },
      ...(isVertical ? verticalStyles.close : horizontalStyles.close),
    },
  };
});

export const alertTheme = defineMultiStyleConfig({
  baseStyle,
  sizes: {
    sm: definePartsStyle(props => {
      const isVertical = props.orientation === `vertical`;

      return {
        container: {
          [vars[`padding-right`].variable]: isVertical ? `space.space-050` : `space.space-100`,
          [vars[`padding-left`].variable]: isVertical ? `space.space-100` : `space.space-150`,
          [vars[`padding-top`].variable]: isVertical ? `space.space-100` : `space.space-050`,
          [vars[`padding-bottom`].variable]: isVertical ? `space.space-100` : `space.space-050`,
        },
        icon: {
          fontSize: `xl`,
        },
        close: {
          ...(isVertical ? {m: `space-200`} : {}),
        },
      };
    }),
    md: definePartsStyle(() => ({
      container: {
        [vars[`padding-right`].variable]: `space.space-200`,
        [vars[`padding-left`].variable]: `space.space-200`,
        [vars[`padding-top`].variable]: `space.space-150`,
        [vars[`padding-bottom`].variable]: `space.space-150`,
      },
    })),
    lg: definePartsStyle(props => {
      const isVertical = props.orientation === `vertical`;

      return {
        container: {
          [vars[`padding-right`].variable]: isVertical ? `space.space-100` : `space.space-200`,
          [vars[`padding-left`].variable]: isVertical ? `space.space-200` : `space.space-300`,
          [vars[`padding-top`].variable]: isVertical ? `space.space-200` : `space.space-100`,
          [vars[`padding-bottom`].variable]: isVertical ? `space.space-200` : `space.space-100`,
          borderRadius: `radius-200`,
          gap: `space-200`,
        },
        icon: {
          fontSize: `2rem`,
        },
        actions: {
          gap: `space-100`,
        },
        close: {
          ...(isVertical
            ? {
                m: calc(vars[`padding-top`].reference).divide(2).toString(),
              }
            : {mr: `-space-300`}),
        },
      };
    }),
  },
  variants: {
    primary: definePartsStyle(props => ({
      container: {
        [vars.background.variable]: mode(
          `colors.${light.semantic.colors.background[`primary-inverse`]}`,
          `colors.${dark.semantic.colors.background[`primary-inverse`]}`,
        )(props),
        [vars.color.variable]: `colors.text.primary-inverse`,
      },
    })),
    secondary: {},
  },
  defaultProps: {
    size: `md`,
    variant: `secondary`,
  },
});
